// Leitura e gravação dos avisos da UC (lembretes_faturamento) e do "ciente" por
// fatura (lembretes_faturamento_ciencia). Regras puras em src/lib/avisosUc.ts.
import type { SupabaseClient } from '@supabase/supabase-js';
import { periodoParaGravar, type AteQuando, type AvisoUc } from '../lib/avisosUc';

export interface CienciaAviso {
    id: string;
    lembrete_id: string;
    fatura_id: number;
    mes_referencia: string;
    usuario_nome: string | null;
    created_at: string;
}

export interface AvisoComCiencia extends AvisoUc {
    /** "Ciente" mais recente, em qualquer fatura. */
    ultimaCiencia: CienciaAviso | null;
}

export interface Autor {
    id?: string | null;
    nome: string;
}

/** Status que a tela mostra. 'agrupado' são as cópias mensais antigas juntadas a outro aviso. */
const STATUS_VISIVEIS = ['pendente', 'encerrado', 'concluido', 'descartado'];

export async function buscarAvisosDasUcs(supabase: SupabaseClient, ucIds: (number | string)[], { soPendentes = false } = {}): Promise<AvisoComCiencia[]> {
    const ids = Array.from(new Set(ucIds.map(String))).filter(Boolean);
    if (ids.length === 0) return [];
    let query = supabase
        .from('lembretes_faturamento')
        .select('*')
        .in('uc_id', ids)
        .order('created_at', { ascending: false });
    query = soPendentes ? query.eq('status', 'pendente') : query.in('status', STATUS_VISIVEIS);
    const { data, error } = await query;
    if (error) throw error;
    const avisos = (data || []) as AvisoUc[];
    const ciencias = await buscarCiencias(supabase, avisos.map(a => a.id));
    const ultima = new Map<string, CienciaAviso>();
    for (const c of ciencias) {
        const atual = ultima.get(c.lembrete_id);
        if (!atual || c.created_at > atual.created_at) ultima.set(c.lembrete_id, c);
    }
    return avisos.map(a => ({ ...a, ultimaCiencia: ultima.get(a.id) ?? null }));
}

export async function buscarCiencias(supabase: SupabaseClient, avisoIds: string[], faturaId?: number | string | null): Promise<CienciaAviso[]> {
    if (avisoIds.length === 0) return [];
    let query = supabase
        .from('lembretes_faturamento_ciencia')
        .select('id, lembrete_id, fatura_id, mes_referencia, usuario_nome, created_at')
        .in('lembrete_id', avisoIds);
    if (faturaId != null) query = query.eq('fatura_id', faturaId);
    const { data, error } = await query;
    // Sem a tabela (banco ainda sem a migration 20261104120000), a tela segue sem o "ciente".
    if (error) {
        console.error('Erro ao ler o "ciente" dos avisos:', error);
        return [];
    }
    return (data || []) as CienciaAviso[];
}

export async function marcarCiente(supabase: SupabaseClient, aviso: Pick<AvisoUc, 'id'>, faturaId: number | string, mesReferencia: string, autor: Autor): Promise<void> {
    const { error } = await supabase.from('lembretes_faturamento_ciencia').upsert({
        lembrete_id: aviso.id,
        fatura_id: Number(faturaId),
        mes_referencia: mesReferencia,
        usuario_id: autor.id ?? null,
        usuario_nome: autor.nome,
    }, { onConflict: 'lembrete_id,fatura_id', ignoreDuplicates: true });
    if (error) throw error;
}

export async function desmarcarCiente(supabase: SupabaseClient, cienciaId: string): Promise<void> {
    const { error } = await supabase.from('lembretes_faturamento_ciencia').delete().eq('id', cienciaId);
    if (error) throw error;
}

/** Tira o aviso de todas as faturas daqui para frente. O motivo fica registrado. */
export async function encerrarAviso(supabase: SupabaseClient, avisoId: string, motivo: string, autor: Autor): Promise<void> {
    const { error } = await supabase
        .from('lembretes_faturamento')
        .update({
            status: 'encerrado',
            motivo_descarte: motivo.trim(),
            data_conclusao: new Date().toISOString(),
            encerrado_por_id: autor.id ?? null,
            encerrado_por_nome: autor.nome,
        })
        .eq('id', avisoId);
    if (error) throw error;
}

/** Muda até quando o aviso vale (renovar, deixar sem prazo ou encurtar). O início fica. */
export async function alterarPrazoDoAviso(supabase: SupabaseClient, aviso: AvisoUc, inicio: string, ate: AteQuando): Promise<void> {
    const periodo = periodoParaGravar(inicio, ate);
    if (typeof periodo === 'string') throw new Error(periodo);
    const { error } = await supabase
        .from('lembretes_faturamento')
        .update(periodo)
        .eq('id', aviso.id)
        .eq('status', 'pendente');
    if (error) throw error;
}
